import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

export type PaginationModel = {
  page: number;
  pageSize: number;
};

export type SetPaginationModel = React.Dispatch<
  React.SetStateAction<PaginationModel>
>;

type UsePaginationModelConfig = {
  defaultPageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
};

type UsePaginationModelReturn = {
  paginationModel: PaginationModel;
  setPaginationModel: SetPaginationModel;
};

/**
 * Custom hook to manage DataGrid pagination with URL sync
 * @param config - Configuration object with default page size and callbacks
 * @returns Object containing paginationModel state and setter
 */
export const usePaginationModel = (
  config: UsePaginationModelConfig = {},
): UsePaginationModelReturn => {
  const { defaultPageSize = 10, onPageSizeChange } = config;
  const [searchParams, setSearchParams] = useSearchParams();

  const [pageSize, setPageSize] = useState<number>(defaultPageSize);

  // Derive pagination model from URL and pageSize state
  const paginationModel = useMemo(() => {
    const pageParam = Number(searchParams.get("page")) || 1;
    return { page: Math.max(0, pageParam - 1), pageSize };
  }, [searchParams, pageSize]);

  // Handle pagination model updates
  const setPaginationModel: SetPaginationModel = (update) => {
    const nextModel =
      typeof update === "function"
        ? (update as (prev: PaginationModel) => PaginationModel)(
            paginationModel,
          )
        : update;

    const next = new URLSearchParams(window.location.search);
    next.set("page", String(nextModel.page + 1));
    next.delete("pageSize");
    setSearchParams(next, { replace: true });

    // Handle page size changes
    if (nextModel.pageSize && nextModel.pageSize !== pageSize) {
      setPageSize(nextModel.pageSize);
      onPageSizeChange?.(nextModel.pageSize);
    }
  };

  // Ensure page parameter exists in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasPage = params.has("page");
    if (!hasPage) {
      params.set("page", "1");
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    paginationModel,
    setPaginationModel,
  };
};
